import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { catchError, concatMap, map, share, switchMap } from 'rxjs/operators';
import { Actions, Effect, ofType } from '@ngrx/effects';

import { AppState } from '../../interfaces/app-state.interface';
import { TrackingActions } from './../tracking/tracking.action';
import { InfoproductsAction } from './infoproducts.action';
import { InfoproductsCourseService } from '../../services/infoproducts/infoproducts-course.service';
import { InfoproductsStrategyService } from '../../services/infoproducts/infoproducts-strategy.service';
import { InfoproductsPurchaseService } from '../../services/infoproducts/infoproducts-purchase.service';

@Injectable()
export class InfoproductsEffects {
  @Effect()
  public setLessonComplete$: Observable<
    InfoproductsAction
  > = this.actions$.pipe(
    ofType(InfoproductsAction.setLessonComplete),
    concatMap(({ payload }) =>
      this.infoproductsCourseService
        .setLessonComplete(payload.courseId, payload.moduleId, payload.lessonId)
        .pipe(
          map(result => ({
            type: InfoproductsAction.setLessonCompleteSuccess,
            payload: result
          })),
          catchError(error =>
            of({
              type: InfoproductsAction.setLessonCompleteError,
              payload: { error }
            })
          )
        )
    ),
    share()
  );

  /**
   * Busca todos os cursos disponíveis
   */
  @Effect()
  public getAvailableCourses$: Observable<
    InfoproductsAction
  > = this.actions$.pipe(
    ofType(InfoproductsAction.getAvailableCourses),
    switchMap(() =>
      Observable.fromPromise(
        this.infoproductsCourseService.getAvailableCourses()
      )
        .map(payload => {
          return {
            payload,
            type: InfoproductsAction.getAvailableCoursesSuccess
          };
        })
        .catch(({ message }) =>
          Observable.of({
            type: InfoproductsAction.getAvailableCoursesError,
            payload: message
          })
        )
    ),
    share()
  );

  /**
   * Busca todos os cursos disponíveis
   */
  @Effect()
  public getAvailableStrategies$: Observable<
    InfoproductsAction
  > = this.actions$.pipe(
    ofType(InfoproductsAction.getAvailableStrategies),
    switchMap(() =>
      Observable.fromPromise(
        this.infoproductsStrategyService.getAvailableStrategies()
      )
        .map(payload => ({
          payload,
          type: InfoproductsAction.getAvailableStrategiesSuccess
        }))
        .catch(({ message }) =>
          Observable.of({
            type: InfoproductsAction.getAvailableStrategiesError,
            payload: message
          })
        )
    ),
    share()
  );

  /**
   * Busca os produtos adquiridos do cliente
   */
  @Effect()
  public getCustomerProducts$: Observable<
    InfoproductsAction
  > = this.actions$.pipe(
    ofType(InfoproductsAction.getCustomerProducts),
    switchMap(() =>
      Observable.fromPromise(
        this.infoproductsPurchaseService.getCustomerProducts()
      )
        .map(payload => ({
          payload,
          type: InfoproductsAction.getCustomerProductsSuccess
        }))
        .catch(({ message }) =>
          Observable.of({
            type: InfoproductsAction.getCustomerProductsError,
            payload: message
          })
        )
    ),
    share()
  );

  /**
   * Busca as subscriptions que o usuário assinou
   */
  @Effect()
  public trialRecommendations$: Observable<
    InfoproductsAction
  > = this.actions$.pipe(
    ofType(InfoproductsAction.trialRecommendation),
    switchMap(action => {
      return this.infoproductsPurchaseService
        .trialRecommendation(action.payload)
        .map(trialPurchase => {
          this.store.dispatch({
            type: TrackingActions.trackEvents,
            payload: {
              type: 'Trial Started',
              events: {
                product: action.payload.productName,
                source: window.location.href,
                isNewCustomer: false
              }
            }
          });

          return {
            type: InfoproductsAction.trialRecommendationSuccess,
            payload: trialPurchase
          };
        })
        .catch(({ error: { error }, message }) =>
          Observable.of({
            type: InfoproductsAction.trialRecommendationError,
            payload: (error && error.message) || message
          })
        );
    }),
    share()
  );

  constructor(
    private actions$: Actions<InfoproductsAction>,
    private infoproductsCourseService: InfoproductsCourseService,
    private infoproductsPurchaseService: InfoproductsPurchaseService,
    private infoproductsStrategyService: InfoproductsStrategyService,
    private store: Store<AppState>
  ) {}
}
